/* eslint-disable complexity */
import { Schemas } from '~/api/gen'
import { Divider } from '~/components/elements/Divider'
import { NoResultsBeforeNiau } from '~/components/elements/NoResultsBeforeNiau'
import { MemberList } from '~/components/modules/MemberList'
import { Pagination } from '~/components/modules/Pagination'
import { useMember } from '~/hooks/useMember'
import { usePaginationProps } from '~/hooks/usePaginationProps'
import { FrequentlyUsedCoordinateItems } from '../shared/memberDetail/components/FrequentlyUsedCoordinateItems'
import { FrequentlyUsedTags } from '../shared/memberDetail/components/FrequentlyUsedTags'
import { Header } from '../shared/search/components/Header'
import { FETCH_COUNT_PER_PAGE } from './constants'
import { useMemberFollowings } from './hooks/useMemberFollwings'

export type Props = {
  userName: string
  pageNumber: number
}

const Title: React.FC<{
  userName: string
  nickname: string
  followingCount: number
  shop: Schemas.shop_with_brands | undefined
}> = ({ userName, nickname, followingCount, shop }) => (
  <>
    <h1 className="text-center text-[16px] xl:hidden">
      {nickname}がフォロー ({followingCount})
    </h1>
    <div className="hidden flex-col border-b border-gray-300 bg-white xl:flex">
      <div className="w-full max-w-[990px] self-center">
        <Header
          title={`${nickname}がフォロー`}
          resultCount={followingCount.toLocaleString()}
          breadcrumbList={[
            shop
              ? {
                  name: shop.name,
                  path: `/shop/${shop.id}/`,
                  disableTranslation: true,
                }
              : [],
            {
              name: nickname,
              path: `/${userName}/`,
              disableTranslation: true,
            },
            {
              name: 'フォロー一覧',
            },
          ].flat()}
        />
      </div>
    </div>
  </>
)

const Results: React.FC<{
  memberList: Schemas.get_members_user_name_followings_v1_response['members']
}> = ({ memberList }) => <MemberList memberList={memberList} />

export const MemberFollowList: React.FC<Props> = ({ userName, pageNumber }) => {
  const member = useMember(userName)
  const memberFollowings = useMemberFollowings(userName, pageNumber)
  const totalPage = Math.ceil(
    Math.min(
      memberFollowings?.total_count ?? 0,
      memberFollowings?.max_count ?? 0
    ) / FETCH_COUNT_PER_PAGE
  )
  const paginationProps = usePaginationProps(pageNumber, totalPage, true)
  if (member === undefined || memberFollowings === undefined) return null

  const frequentlyUsedTagsExist = member.frequently_used_tags.length > 0
  const frequentlyUsedCoordinateItemsExist =
    member.frequently_used_coordinate_items.length > 0

  return (
    <main>
      <div className="pb-[10px] pt-5 xl:pb-[35px] xl:pt-0">
        <Title
          userName={member.user_name}
          nickname={member.nick_name}
          followingCount={memberFollowings.total_count}
          shop={member.shop}
        />
      </div>
      {memberFollowings.total_count === 0 ? (
        <NoResultsBeforeNiau className="pb-[30px] pt-[45px] xl:pb-[100px] xl:pt-[50px]">
          まだフォローしていません
        </NoResultsBeforeNiau>
      ) : (
        <div className="mx-auto flex flex-col gap-y-5 xl:w-[990px] xl:gap-y-[50px]">
          <Results memberList={memberFollowings.members} />
          <Pagination {...paginationProps} />
        </div>
      )}
      {frequentlyUsedTagsExist || frequentlyUsedCoordinateItemsExist ? (
        <div className="mx-auto xl:w-[990px]">
          <div className="hidden xl:block xl:pt-7">
            <Divider />
          </div>
          <section className="grid gap-y-[22px] px-4 pt-10 xl:gap-y-7 xl:px-0 xl:pt-[30px]">
            {frequentlyUsedTagsExist && (
              <FrequentlyUsedTags
                nickName={member.nick_name}
                frequentlyUsedTags={member.frequently_used_tags}
              />
            )}
            {frequentlyUsedCoordinateItemsExist && (
              <FrequentlyUsedCoordinateItems
                nickName={member.nick_name}
                frequentlyUsedCoordinateItems={
                  member.frequently_used_coordinate_items
                }
              />
            )}
          </section>
        </div>
      ) : null}
    </main>
  )
}
